import { normalizeDocument, normalizeDocumentToLatestSchema } from '@gitbook/doc-core';
import {
    type ContentBranch,
    type ContentBranchHistory,
    type ContentBranchHistoryV2,
    type ContentBranchMergeResult,
    type ContentBranchMergeStrategy,
    type ContentComputerFactory,
    type ContentComputerInstance,
    type ContentOperation,
    type DataSyncDecoderHandlers,
    type DuplicateContentParams,
    dummyContentComputer,
    getFileBlobDownloadURL,
    type MaskedRevision,
    normalizeRevision,
    type Revision,
    type RevisionFileBlobMetadata,
    type RevisionMetadataUpdate,
    randomUUID,
} from '@gitbook/hive-core';
import {
    type Document,
    deserializeFromProtocolBuffer,
    serializeToProtocolBuffer,
} from '@gitbook/slate';
import {
    createHTTPError,
    type HTTPError,
    HTTPStatusCode,
    Logger,
    Trace,
} from '@gitbook/util-debug';
import type { SuspenceBrowserCache } from '@gitbook/util-suspense-cache';
import type { MaybePromise } from '@gitbook/util-ts';
import { WebSocket, type WebSocketOptions } from '@gitbook/util-websocket';

import { type Blob, fetch, fetchOptions, Headers, type RequestInit, type Response } from './fetch';
import {
    type FetchHiveContentAuthTokenCallback,
    type HiveAuthTokenCacheData,
    HiveAuthTokenManager,
} from './HiveAuthTokenManager';
import { HiveContentOperation } from './HiveContentOperation';
import { HiveContentStore, type HiveContentStoreOptions } from './HiveContentStore';
import type { HiveDataSyncBrowserCache } from './HiveDataSyncBrowserCache';
import { createV1Listener } from './HiveDataSyncListener';
import { HiveDataSyncManager } from './HiveDataSyncManager';
import { HiveDataSyncV2Manager } from './HiveDataSyncV2Manager';
import type { HiveV2Socket } from './HiveV2Socket';
import { createObjectStorageFetch, type ObjectStorageFetchers } from './objectStorage';

const logger = Logger('hive:client');
const isGlobalEdgeClient =
    typeof globalThis.window !== 'undefined' || process.env.NODE_ENV === 'test';

export type HiveEdgeLocation =
    | 'wnam'
    | 'enam'
    | 'sam'
    | 'weur'
    | 'eeur'
    | 'apac'
    | 'oc'
    | 'afr'
    | 'me';

export type HiveClientOptions = {
    /**
     * URL endpoint of the Hive instance.
     * It can be an async function to lazy-load a Hive worker instance only when needed.
     */
    endpoint: string | ((contentId: string) => MaybePromise<HiveEndpoint | string>);

    /**
     * Optionally override the internal implementation of object storage used by the client.
     * This should only be used for testing.
     */
    objectStorage?: ObjectStorageFetchers;

    /**
     * Callback to fetch a auth token for a specific contentId.
     * The token will be cached by the client.
     */
    getAuthToken: FetchHiveContentAuthTokenCallback;

    /**
     * Headers to append to every request made by the client.
     */
    headers?: Record<string, string>;

    /**
     * Location hint to indicate to hive for allocating edge resources (DOs) as close as possible to the end user.
     * This parameter can be passed when the hive client is instantiated in a backend as a response to a end-user request.
     */
    locationHint?: HiveEdgeLocation;

    /**
     * When used in the browser, pass a `SuspenceBrowserCache` to automatically
     * persist tokens in the browser's cache IndexedDB.
     */
    tokensBrowserCache?: SuspenceBrowserCache<HiveAuthTokenCacheData>;

    /**
     * Allow the client to connect to Websocket, even if it's not running at the edge.
     * To avoid mistake of connecting to websocket from the backend; this option is disabled by default.
     */
    dangerouslyAllowWebsocket?: boolean;

    /**
     * Allow the client to perform edge operations even when not running at the edge.
     * Use for testing and server-side use only.
     */
    dangerouslyAllowEdgeOperations?: boolean;

    /**
     * Options to pass through to the WebSocket connections.
     */
    websocketOptions?: WebSocketOptions;

    /**
     * Factory to create a content computer.
     */
    compute?: ContentComputerFactory;

    /**
     * Optionally supply a content browser cache for this client.
     */
    dataSyncCache?: HiveDataSyncBrowserCache;

    /**
     * Resolver/value indicating if a content is using Hive V2 DataSync.
     * Optional, but must be provided if opening DataSync connections.
     */
    isHiveV2?: boolean | ((contentId: string) => MaybePromise<boolean>);

    /**
     * Provide options for the content store.
     */
    store?: HiveContentStoreOptions;

    /**
     * Datasync ttl in milliseconds. It determines how long a datasync connection can be idle before being closed automatically.
     */
    dataSyncTTL?: number;
};

export interface HiveEndpoint {
    objectStorageBaseURL: string;
    runtimeBaseURL: string;
    blobBaseURL: string;

    /**
     * If true, the contentId will be stripped from the URL path when making requests.
     */
    stripContentId?: boolean;
}

export interface GetRevisionOptions<MaskKeys extends (keyof Revision)[] | undefined> {
    /**
     * If false, the revision could be fetched from a cache where metadata are not up-to-date.
     * Metadata are mutable `git` informations stored on revision/files/pages.
     * @default true
     */
    metadata?: boolean;

    /**
     * Mask of properties to include in the response.
     * It should be used to reduce the size of the response when only a few properties are needed.
     */
    mask?: MaskKeys;
}

export type RequestOptions = {
    /**
     * How many times should the request be retried in case of unexpected network error.
     * The request is not retried in case of HTTP errors.
     * @default 3
     */
    retries?: number;
    timeoutMs?: number;
};

/**
 * Client to interact with a Hive instance.
 */
export class HiveClient {
    public readonly auth: HiveAuthTokenManager;
    public readonly store: HiveContentStore;
    public readonly sync: HiveDataSyncManager;
    public readonly compute: ContentComputerFactory | undefined;

    public readonly SocketManager: HiveDataSyncV2Manager;

    public readonly clientId: string;

    /**
     * As durable objects are allocated as close as possible to the client,
     * we avoid making requests to durable objects from our backend to make sure the durable objects will be allocated
     * in the same region as the user and not our backend.
     */
    private isRunningAtEdge: boolean | HiveEdgeLocation = isGlobalEdgeClient;
    public readonly dangerouslyAllowWebsocket: boolean;
    public readonly getEndpoint: (contentId: string) => Promise<HiveEndpoint>;
    private readonly webSocketOptions: WebSocketOptions | undefined;
    private readonly objectStorage: ObjectStorageFetchers;
    private readonly headers: Record<string, string>;
    private readonly isHiveV2: HiveClientOptions['isHiveV2'];
    private readonly isHiveV2Cache = new Map<string, Promise<boolean>>();

    constructor(options: HiveClientOptions) {
        const {
            endpoint,
            getAuthToken,
            tokensBrowserCache,
            locationHint,
            dangerouslyAllowWebsocket = false,
            dangerouslyAllowEdgeOperations = false,
            compute,
            dataSyncCache,
            isHiveV2,
            websocketOptions,
            objectStorage,
            store,
            headers,
        } = options;

        if (dangerouslyAllowEdgeOperations) {
            this.isRunningAtEdge = true;
        }

        this.headers = headers ?? {};
        this.isHiveV2 = isHiveV2;
        this.webSocketOptions = websocketOptions;
        this.objectStorage =
            objectStorage ??
            createObjectStorageFetch(async (contentId) => {
                const ep = await this.getEndpoint(contentId);
                return ep.objectStorageBaseURL;
            }, this.request.bind(this));

        const cache = new Map<string, HiveEndpoint>();
        this.getEndpoint = async (contentId: string) => {
            if (typeof endpoint === 'string') {
                return {
                    runtimeBaseURL: endpoint,
                    objectStorageBaseURL: endpoint,
                    blobBaseURL: endpoint,
                };
            }

            const cached = cache.get(contentId);
            if (cached) {
                return cached;
            }

            let result = await endpoint(contentId);

            if (typeof result === 'string') {
                result = {
                    runtimeBaseURL: result,
                    objectStorageBaseURL: result,
                    blobBaseURL: result,
                };
            }

            cache.set(contentId, result);
            return result;
        };

        this.auth = new HiveAuthTokenManager(getAuthToken, {
            browserCache: tokensBrowserCache,
        });
        this.store = new HiveContentStore(this, store);
        this.compute = compute;
        this.sync = new HiveDataSyncManager(this, {
            dataSyncCache,
            ttl: options.dataSyncTTL,
        });

        this.clientId = randomUUID();

        if (locationHint) {
            this.isRunningAtEdge = locationHint;
        }

        this.dangerouslyAllowWebsocket = dangerouslyAllowWebsocket;

        this.SocketManager = new HiveDataSyncV2Manager((contentId) => async () => {
            const endpoint = await this.getEndpoint(contentId);
            const resourcePath = getContentResourcePath(
                contentId,
                '/datasync/v2',
                {},
                endpoint.stripContentId
            );
            const authToken = await this.auth.getToken(contentId);

            const url = new URL(resourcePath, endpoint.runtimeBaseURL);
            url.protocol = url.protocol.replace('http', 'ws');
            url.searchParams.set('authToken', authToken);

            return url;
        });
    }

    /**
     * Resolve whether a content should use Hive V2 DataSync.
     */
    public resolveIsHiveV2(contentId: string): Promise<boolean> | undefined {
        if (this.isHiveV2 === undefined) {
            return undefined;
        }

        if (typeof this.isHiveV2 === 'boolean') {
            return Promise.resolve(this.isHiveV2);
        }

        const cached = this.isHiveV2Cache.get(contentId);
        if (cached) {
            return cached;
        }

        const resolve = Promise.resolve(this.isHiveV2(contentId)).catch((error) => {
            this.isHiveV2Cache.delete(contentId);
            throw error;
        });
        this.isHiveV2Cache.set(contentId, resolve);

        return resolve;
    }

    /**
     * Close all connections.
     */
    public async close() {
        await this.sync.closeAll();
    }

    /**
     * Force an immediate reconnection of all realtime connections that are waiting on a
     * scheduled (backoff) reconnection, bypassing the remaining delay. Safe to call at
     * any time — it is a no-op for connections that are healthy or intentionally closed.
     *
     * Used to recover quickly when the page regains focus after a disconnection (e.g. the
     * computer woke up or the tab was backgrounded).
     */
    public reconnect(): void {
        this.SocketManager.reconnectAllNow();
    }

    /**
     * Clear all data related to the user session.
     * It should be called when the user logs out.
     */
    public async clearAuthCache() {
        await Promise.all([this.auth.clearCache(), this.sync.clearCache()]);
        await this.close();
    }

    /**
     * Get a realtime connection.
     */
    public getSocket(
        contentId: string,
        path: string,
        pathParams: Record<string, string>,
        decoderHandlers: DataSyncDecoderHandlers,
        queryParams?: Record<string, string>
    ): WebSocket | HiveV2Socket {
        if (!this.dangerouslyAllowWebsocket && (!this.isRunningAtEdge || !isGlobalEdgeClient)) {
            throw new Error('HiveClient#getSocket can only be called from the edge');
        }

        if (this.SocketManager.hasSocketForContent(contentId)) {
            return this.getV2Socket(contentId, path, pathParams, decoderHandlers);
        }

        const getSocketURL = async () => {
            const endpoint = await this.getEndpoint(contentId);
            const resourcePath = getContentResourcePath(
                contentId,
                path,
                pathParams,
                endpoint.stripContentId
            );
            const authToken = await this.auth.getToken(contentId);

            const url = new URL(resourcePath, endpoint.runtimeBaseURL);
            url.protocol = url.protocol.replace('http', 'ws');

            if (queryParams) {
                for (const [key, value] of Object.entries(queryParams)) {
                    url.searchParams.set(key, value);
                }
            }
            url.searchParams.set('authToken', authToken);

            return url;
        };
        const ws = new WebSocket(getSocketURL, this.webSocketOptions);

        createV1Listener(decoderHandlers, ws);

        return ws;
    }

    public getV2Socket(
        contentId: string,
        path: string,
        pathParams: Record<string, string>,
        decoderHandlers: DataSyncDecoderHandlers
    ): HiveV2Socket {
        return this.SocketManager.getSocketForContent(
            contentId,
            getContentResourcePath(contentId, path, pathParams),
            decoderHandlers,
            this.webSocketOptions
        );
    }

    /**
     * Get the current persisted state of a branch by its ID.
     * This read does not flush pending realtime edits.
     */
    public async getBranch(contentId: string, branchId: string): Promise<ContentBranch> {
        const endpoint = await this.getEndpoint(contentId);
        const response = await this.request(
            {
                contentId,
                baseURL: endpoint.runtimeBaseURL,
                path: '/branches/:branchId',
                params: {
                    path: { branchId },
                },
            },
            undefined,
            { stripContentId: endpoint.stripContentId }
        );
        const branch = (await response.json()) as ContentBranch;

        return branch;
    }

    /**
     * Create a new branch.
     */
    public async createBranchV2(contentId: string, branchId: string) {
        const endpoint = await this.getEndpoint(contentId);

        // In the V2, we have a dedicated endpoint to create a branch.
        const response = await this.request(
            {
                baseURL: endpoint.runtimeBaseURL,
                contentId,
                path: '/branches/:branchId',
                params: {
                    path: { branchId },
                },
            },
            { method: 'POST' },
            { stripContentId: endpoint.stripContentId }
        );

        const branch = await response.json();
        return branch;
    }

    /**
     * Create a new content with a default branch.
     * Only available in Hive V2
     */
    public async createContentV2(
        contentId: string,
        initialContent:
            | {
                  kind: 'revision';
                  revision: Revision;
              }
            | { kind: 'duplicate'; revision: string; contentId: string }
            | {
                  kind: 'template';
                  contentId: string;
                  params?: DuplicateContentParams;
              },
        options: RequestOptions = {}
    ): Promise<{ revision: Revision }> {
        const endpoint = await this.getEndpoint(contentId);
        const response = await this.request(
            {
                baseURL: endpoint.runtimeBaseURL,
                contentId,
                path: '/',
            },
            {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    initialContent,
                }),
            },
            { stripContentId: endpoint.stripContentId, ...options }
        );
        const branch = (await response.json()) as { revision: Revision };
        return branch;
    }

    /**
     * Get the latest saved revision on a branch.
     */
    public getBranchRevision(contentId: string, branchId: string): Promise<Revision>;
    public getBranchRevision<MaskKey extends keyof Revision>(
        contentId: string,
        branchId: string,
        mask: MaskKey[]
    ): Promise<MaskedRevision<MaskKey>>;
    public async getBranchRevision<MaskKey extends keyof Revision>(
        contentId: string,
        branchId: string,
        mask?: MaskKey[]
    ): Promise<Revision> {
        const endpoint = await this.getEndpoint(contentId);
        const response = await this.request(
            {
                baseURL: endpoint.runtimeBaseURL,
                contentId,
                path: '/branches/:branchId/revision',
                params: {
                    path: { branchId },
                    query: mask
                        ? {
                              mask: mask.join(','),
                          }
                        : {},
                },
            },
            {
                method: 'GET',
            },
            { stripContentId: endpoint.stripContentId }
        );
        const revision = (await response.json()) as Revision;

        return revision;
    }

    /**
     * Save the latest changes made on a branch to the cold storage.
     */
    public async saveBranch(contentId: string, branchId: string): Promise<Revision> {
        return this.sync.withReadOnlyDataSyncs(
            async () => {
                const endpoint = await this.getEndpoint(contentId);
                const response = await this.request(
                    {
                        baseURL: endpoint.runtimeBaseURL,
                        contentId,
                        path: '/branches/:branchId/save',
                        params: {
                            path: { branchId },
                        },
                    },
                    {
                        method: 'POST',
                    },
                    { stripContentId: endpoint.stripContentId }
                );
                const revision = (await response.json()) as Revision;

                return revision;
            },
            (sync) => sync.contentId === contentId
        );
    }

    /**
     * Get the history of a branch
     */
    public async getBranchHistory(
        contentId: string,
        branchId: string,
        options: {
            limit?: number;
            page?: Revision['id'];
            git?: boolean;
            refresh?: boolean;
        } = {}
    ): Promise<ContentBranchHistory | ContentBranchHistoryV2> {
        const { limit = 10, page, git, refresh } = options;
        const endpoint = await this.getEndpoint(contentId);
        const response = await this.request(
            {
                contentId,
                baseURL: endpoint.objectStorageBaseURL,
                path: '/branches/:branchId/history',
                params: {
                    path: { branchId },
                    query: {
                        limit: limit.toString(),
                        ...(page ? { page } : {}),
                        ...(git !== undefined ? { git: `${git}` } : {}),
                        ...(refresh !== undefined ? { refresh: `${refresh}` } : {}),
                    },
                },
            },
            undefined
        );
        const branchHistory = (await response.json()) as
            | ContentBranchHistory
            | ContentBranchHistoryV2;

        return branchHistory;
    }

    /**
     * Get a revision by its ID.
     */
    public async getRevision<MaskKeys extends (keyof Revision)[] | undefined>(
        contentId: string,
        revisionId: string,
        options: GetRevisionOptions<MaskKeys> = {}
    ): Promise<MaskKeys extends (keyof Revision)[] ? MaskedRevision<MaskKeys[number]> : Revision> {
        const { revision, normalized } = await this.objectStorage.getRevision(
            contentId,
            revisionId,
            options
        );

        if (!options.mask && !normalized) {
            const trace = new Trace('hive:revision:normalize');
            trace.setLabels({ contentId, revisionId });
            trace.wrapSync(() => normalizeRevision(revision));
        }

        // @ts-ignore
        return revision;
    }

    /**
     * Merge a branch into its origin branch.
     */
    public async mergeBranch(
        contentId: string,
        branchId: string,
        strategy?: ContentBranchMergeStrategy,
        options?: RequestOptions
    ): Promise<ContentBranchMergeResult> {
        if (!this.isRunningAtEdge) {
            throw new Error('HiveClient#mergeBranch can only be called from the edge');
        }

        const endpoint = await this.getEndpoint(contentId);
        const response = await this.request(
            {
                baseURL: endpoint.runtimeBaseURL,
                contentId,
                path: '/branches/:branchId/merge',
                params: {
                    path: { branchId },
                },
            },
            {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: strategy
                    ? JSON.stringify({
                          strategy,
                      })
                    : undefined,
            },
            { ...options, stripContentId: endpoint.stripContentId }
        );
        const result = (await response.json()) as ContentBranchMergeResult;
        return result;
    }

    /**
     * Rollback a branch to a previous revision.
     */
    public async rollbackBranch(
        contentId: string,
        branchId: string,
        params: {
            toRevisionId: string;
        },
        options?: RequestOptions
    ): Promise<void> {
        const endpoint = await this.getEndpoint(contentId);
        await this.request(
            {
                contentId,
                baseURL: endpoint.runtimeBaseURL,
                path: '/branches/:branchId/rollback',
                params: {
                    path: { branchId },
                },
            },
            {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(params),
            },
            { ...options, stripContentId: endpoint.stripContentId }
        );

        this.store.clearHistoryCache();
    }

    /**
     * Update a branch with changes made in its origin branch.
     */
    public async updateBranch(contentId: string, branchId: string, options?: RequestOptions) {
        const endpoint = await this.getEndpoint(contentId);
        const response = await this.request(
            {
                contentId,
                baseURL: endpoint.runtimeBaseURL,
                path: '/branches/:branchId/update',
                params: {
                    path: { branchId },
                },
            },
            {
                method: 'POST',
            },
            { ...options, stripContentId: endpoint.stripContentId }
        );
        const result = (await response.json()) as { revisionId: string; conflicts: boolean };
        return result;
    }

    /**
     * Update a branch to point to a specific revision.
     */
    public async checkoutBranch(
        contentId: string,
        branchId: string,
        params: {
            /**
             * ID of the revision to checkout.
             */
            revisionId: string;
            /**
             * Force replace the branch with the revision.
             * This will discard any changes made on the branch.
             *
             * If false, the operation will be reject with a code of 409 if the branch has uncommitted changes.
             * @default false
             */
            force?: boolean;

            /**
             * Only applies to the V2 where firestore writes are not awaited.
             * If true, wait for Firestore writes to propagate before returning.
             */
            waitForFirestore?: boolean;
        },
        options: RequestOptions & {
            /**
             * ID of the running operation, the checkout is part of.
             */
            operationId?: string;
        } = {}
    ): Promise<Revision> {
        const endpoint = await this.getEndpoint(contentId);
        const response = await this.request(
            {
                contentId,
                baseURL: endpoint.runtimeBaseURL,
                path: '/branches/:branchId/checkout',
                params: {
                    path: { branchId },
                },
            },
            {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...createContentOperationHeaders(options.operationId),
                },
                body: JSON.stringify(params),
            },
            {
                ...options,
                stripContentId: endpoint.stripContentId,
            }
        );

        return response.json() as Promise<Revision>;
    }

    /**
     * Fetch a document by its ID, and returns it as a raw buffer.
     */
    public async getDocumentBuffer(
        contentId: string,
        documentId: string,
        options: { noCache?: boolean } = {}
    ) {
        return this.objectStorage.getDocumentBuffer(contentId, documentId, options);
    }

    /**
     * Fetch a document by its ID.
     */
    public async getDocument(
        contentId: string,
        documentId: string,
        options: { noCache?: boolean } = {}
    ) {
        const { buffer, normalized } = await this.getDocumentBuffer(contentId, documentId, options);
        const decodeTrace = new Trace('hive:document:decode');
        decodeTrace.setLabels({ contentId, documentId, bytes: buffer.byteLength });
        const document = decodeTrace.wrapSync(() => deserializeFromProtocolBuffer(buffer));

        if (normalized) {
            return document;
        }

        // Currently done in the client to avoid having to do it in the worker
        try {
            const trace = new Trace('hive:document:normalize');
            trace.setLabels({ contentId, documentId });
            return trace.wrapSync(() => normalizeDocumentToLatestSchema(document));
        } catch (error) {
            logger.warning('Failed to normalize fetched document', {
                contentId,
                documentId,
                error,
            });
            throw error;
        }
    }

    /**
     * Fetch a page's current server document through the Hive runtime HTTP endpoint.
     * Unlike store.getBranchPageDocument(), this makes a network request instead of preferring
     * an existing local sync or saved page metadata. Local edits not yet sent are not included.
     * Requires an edge-enabled client; for ongoing editing, open a page sync instead.
     */
    public async getPageDocument(
        contentId: string,
        branchId: string,
        pageId: string
    ): Promise<Document> {
        if (!this.isRunningAtEdge) {
            throw new Error('HiveClient#getPageDocument can only be called from the edge');
        }

        const endpoint = await this.getEndpoint(contentId);
        const response = await this.request(
            {
                contentId,
                baseURL: endpoint.runtimeBaseURL,
                path: '/branches/:branchId/pages/:pageId/document',
                params: {
                    path: { branchId, pageId },
                },
            },
            undefined,
            { stripContentId: endpoint.stripContentId }
        );
        const bodyTrace = new Trace('hive:document:body');
        bodyTrace.setLabels({ contentId, branchId, pageId });
        const arrayBuffer = await bodyTrace.wrap(() => response.arrayBuffer());
        const decodeTrace = new Trace('hive:document:decode');
        decodeTrace.setLabels({ contentId, branchId, pageId, bytes: arrayBuffer.byteLength });
        const document = decodeTrace.wrapSync(() =>
            deserializeFromProtocolBuffer(new Uint8Array(arrayBuffer))
        );
        const normalized = response.headers.get('X-Hive-Normalized') === 'true';
        if (normalized) {
            return document;
        }

        // Currently done in the client to avoid having to do it in the worker
        try {
            const trace = new Trace('hive:document:normalize');
            trace.setLabels({ contentId, branchId, pageId });
            return trace.wrapSync(() => normalizeDocumentToLatestSchema(document));
        } catch (error) {
            logger.warning('Failed to normalize fetched page document', {
                contentId,
                error,
                pageId,
            });
            throw error;
        }
    }

    /**
     * Create a document in the storage and returns its ID.
     */
    public async createDocument(
        contentId: string,
        document: Document | Uint8Array,
        options: {
            /** Document ID to use */
            documentId?: string;
            /** Skip normalization (when we know the document has already been normalized) */
            skipNormalization?: boolean;
        } = {}
    ) {
        const body =
            document instanceof Uint8Array
                ? document
                : serializeToProtocolBuffer(
                      options.skipNormalization ? document : normalizeDocument(document),
                      { preserveKeys: true }
                  );

        const result = await this.objectStorage.createDocument(contentId, body, options);
        this.store.clearDocument(contentId, result.documentId);
        return result;
    }

    /**
     * Create a standalone revision in the storage and returns its ID.
     */
    public async createRevision(
        contentId: string,
        revision: Revision,
        options: {
            /** If true, the potential existing revision will be overwriten */
            force?: boolean;
        } = {}
    ) {
        const result = await this.objectStorage.createRevision(contentId, revision, options);
        this.store.clearRevision(contentId, result.revisionId);
        return result;
    }

    /**
     * Create a controller for a content operation.
     */
    public getContentOperation<Payload extends Record<string, string>>(
        contentId: string,
        path: string,
        pathParams: Record<string, string>,
        operationId?: string
    ): HiveContentOperation<Payload> {
        return new HiveContentOperation(this, contentId, path, pathParams, operationId);
    }

    /**
     * Create a controller for a content operation on a branch revision
     */
    public getBranchContentOperation<Payload extends Record<string, string>>(
        contentId: string,
        branchId: string,
        operationId?: string
    ): HiveContentOperation<Payload> {
        return this.getContentOperation(
            contentId,
            '/branches/:branchId/operation',
            {
                branchId,
            },
            operationId
        );
    }

    /**
     * Upload a file blob to the storage and returns its URL.
     */
    public async createFileBlob(
        contentId: string,
        metadata: RevisionFileBlobMetadata,
        blob: Blob | ReadableStream
    ): Promise<{ blobId: string; downloadURL: string }> {
        const { blobId } = await this.objectStorage.createFileBlob(contentId, metadata, blob);
        const endpoint = await this.getEndpoint(contentId);
        return {
            blobId,
            downloadURL: getFileBlobDownloadURL(endpoint.blobBaseURL, contentId, {
                blobId,
                name: metadata.name,
            }),
        };
    }

    /**
     * Update the metadata of a revision.
     */
    public async updateRevisionMetadata(
        contentId: string,
        revisionId: string,
        metadata: RevisionMetadataUpdate
    ) {
        return this.objectStorage.updateRevisionMetadata(contentId, revisionId, metadata);
    }

    /**
     * Duplicate a content, and returns the location of the newly created content.
     */
    public async duplicateContent(
        target: {
            contentId: string;
            branchId: string;
            pageId?: string;
        },
        origin: {
            contentId: string;
            branchId: string;
            pageId?: string;
        },
        options: {
            /**
             * Wait for the duplication to be over.
             * It can result in a longer response time.
             * @default false
             */
            waitForOperation?: boolean;

            /**
             * Params to be applied to the duplicated content.
             */
            params?: DuplicateContentParams;
        } = {}
    ) {
        const { waitForOperation = false, params } = options;

        const endpoint = await this.getEndpoint(target.contentId);
        const response = await this.request(
            {
                contentId: target.contentId,
                baseURL: endpoint.runtimeBaseURL,
                path: '/branches/:branchId/duplicate',
                params: {
                    path: { branchId: target.branchId },
                },
            },
            {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    origin,
                    page: target.pageId,
                    ...(params ? { params } : {}),
                }),
            },
            { stripContentId: endpoint.stripContentId }
        );

        const result = (await response.json()) as {
            contentId: string;
            branchId: string;
            pageId: string;
            operationId?: string;
        };
        if (waitForOperation && result.operationId) {
            await this.waitForOperation(target, result.operationId);
        }

        return result;
    }

    /**
     * Apply a template to content.
     */
    public async applyTemplateV2(input: {
        target: {
            contentId: string;
            branchId: string;
        };
        origin: {
            contentId: string;
        };
        params?: DuplicateContentParams;
    }) {
        const { target, origin, params } = input;

        const endpoint = await this.getEndpoint(target.contentId);
        const response = await this.request(
            {
                contentId: target.contentId,
                baseURL: endpoint.runtimeBaseURL,
                path: '/branches/:branchId/template',
                params: {
                    path: { branchId: target.branchId },
                },
            },
            {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    origin,
                    ...(params ? { params } : {}),
                }),
            },
            { stripContentId: endpoint.stripContentId }
        );

        const result = await response.json();
        return result;
    }

    /**
     * Wait for an operation to be completed.
     */
    public async waitForOperation(
        target: {
            contentId: string;
            branchId: string;
        },
        operationId: string
    ): Promise<ContentOperation | null> {
        const endpoint = await this.getEndpoint(target.contentId);

        try {
            const response = await this.request(
                {
                    contentId: target.contentId,
                    baseURL: endpoint.runtimeBaseURL,
                    path: '/branches/:branchId/operation',
                    params: {
                        path: { branchId: target.branchId },
                        query: { waitFor: operationId },
                    },
                },
                {
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                },
                { stripContentId: endpoint.stripContentId }
            );

            const json = (await response.json()) as ContentOperation;
            return json;
        } catch (error) {
            if ((error as HTTPError).statusCode === HTTPStatusCode.NotFound) {
                return null;
            }

            throw error;
        }
    }

    /**
     * Internal method to trigger an alarm on one of the DurableObjects.
     */
    public async triggerAlarm(
        object: {
            contentId: string;
            branchId: string;
            pageId?: string;
        },
        options = {
            /**
             * If true, the alarm will be triggered even if it is not set.
             * Be careful when using this, as alarms are a core part of DOs and
             * we want our tests to have behaviour as close to production as possible.
             */
            force: false,
        }
    ): Promise<void> {
        const endpoint = await this.getEndpoint(object.contentId);
        if (object.pageId) {
            await this.request(
                {
                    baseURL: endpoint.runtimeBaseURL,
                    contentId: object.contentId,
                    path: '/branches/:branchId/pages/:pageId/document/alarm',
                    params: {
                        path: { branchId: object.branchId, pageId: object.pageId },
                        query: { force: String(options.force) },
                    },
                },
                {
                    method: 'POST',
                },
                { stripContentId: endpoint.stripContentId }
            );
        } else {
            await this.request(
                {
                    baseURL: endpoint.runtimeBaseURL,
                    contentId: object.contentId,
                    path: '/branches/:branchId/alarm',
                    params: {
                        path: { branchId: object.branchId },
                        query: { force: String(options.force) },
                    },
                },
                {
                    method: 'POST',
                },
                { stripContentId: endpoint.stripContentId }
            );
        }
    }

    /**
     * Execute a request.
     */
    public async request(
        target: {
            baseURL: string;
            contentId: string;
            path: string;
            params?: {
                path?: Record<string, string>;
                query?: Record<string, string>;
            };
        },
        requestInit: RequestInit = {},
        options: RequestOptions & {
            stripContentId?: boolean;
        } = {}
    ): Promise<Response> {
        const { retries = 3, timeoutMs } = options;
        const { baseURL, contentId, path, params } = target;
        const { path: pathParams = {}, query: queryParams = {} } = params || {};

        const trace = new Trace(
            `hive:request:${(requestInit.method || 'GET').toLowerCase()}:${path}`
        );
        trace.label('contentId', contentId);
        trace.setLabels({
            clientId: this.clientId,
            locationHint: this.isRunningAtEdge,
            ...params,
        });

        return trace.wrap(async () => {
            const resourcePath = getContentResourcePath(
                contentId,
                path,
                pathParams,
                options.stripContentId
            );
            const authToken = await this.auth.getToken(contentId);

            const url = new URL(resourcePath, baseURL);
            url.search = new URLSearchParams(queryParams).toString();

            const headers = new Headers(requestInit.headers || {});
            headers.set('Authorization', `Bearer ${authToken}`);
            headers.set('X-Client-Id', this.clientId);
            headers.set('X-Content-Id', contentId);

            const traceHeaders = trace.headers();
            for (const [key, value] of Object.entries(traceHeaders)) {
                headers.set(key, value);
            }

            if (typeof this.isRunningAtEdge === 'string') {
                headers.set('X-Hive-Location-Hint', this.isRunningAtEdge);
            }

            for (const [key, value] of Object.entries(this.headers)) {
                headers.set(key, value);
            }

            const response = await retryOnServerError(async () => {
                // Timeout requests after 30s.
                // If the server takes too much time to respond, it can cause
                // our backend execution time (and our bills) to grow too heavily,
                // but we'll also most likely never get a valid response if we wait longer
                // anyway, as it's a sign that Durable Objects are overloaded.

                // We use an AbortController with setTimeout, because using AbortSignal.timeout()
                // seems to be broken in undici and can sometimes throw even though the request
                // succeeded.
                // For instance: https://gitbook-x.sentry.io/issues/4998430936/
                const controller = new AbortController();
                const id = setTimeout(
                    () => {
                        controller.abort();
                    },
                    timeoutMs || 1000 * 30
                );

                const response = await fetch(url.toString(), {
                    ...fetchOptions,
                    ...requestInit,
                    headers,
                    signal: controller.signal,
                });

                // Prevent the controller to throw once the request has finished
                clearTimeout(id);

                return response;
            }, retries);

            const rayId = response.headers.get('cf-ray');
            trace.label('rayId', rayId);

            if (!response.ok) {
                try {
                    let message = '';
                    try {
                        // Response body can be consumed only once. Clone allows us to invoke response.text() in case of errors
                        const body = (await response.clone().json()) as {
                            error: { message: string };
                        };
                        message = body.error.message;
                    } catch (_err) {
                        const text = await response.text();
                        message = `Unexpected text response: ${text}`;
                    }

                    throw createHTTPError.fromStatusCode(response.status, message);
                } catch (err) {
                    if ((err as HTTPError).statusCode) {
                        throw err;
                    }

                    throw new Error(
                        `Unexpected response with status code ${response.status}: ${
                            err instanceof Error ? err.message : String(err)
                        }`
                    );
                }
            }

            // Response body must be consumed to avoid socket error.
            // https://stackoverflow.com/a/77909959
            const clonedResponse = response.clone();
            return clonedResponse;
        });
    }

    /**
     * Get a content computer.
     */
    public getContentComputer(contentId: string): ContentComputerInstance {
        if (!this.compute) {
            return dummyContentComputer;
        }
        return this.compute(contentId);
    }

    /**
     * Return true if the client is configured with a content computer.
     */
    public hasContentComputer(): boolean {
        return Boolean(this.compute);
    }
}

/**
 * Return the content resource full path.
 */
function getContentResourcePath(
    contentId: string,
    path: string,
    params: Record<string, string>,
    stripContentId = false
) {
    for (const [key, value] of Object.entries(params)) {
        path = path.replace(`:${key}`, value);
    }

    if (stripContentId) {
        return path;
    }

    return `content/${contentId}${path}`;
}

/**
 * Create the HTTP headers when a request is done as part of a content operation.
 */
function createContentOperationHeaders(operationId: string | undefined): Record<string, string> {
    if (operationId) {
        return { 'X-Content-Operation-Id': operationId };
    }

    return {};
}

/**
 * Retry an operation if it fails with a network error.
 */
async function retryOnServerError(
    operation: () => Promise<Response>,
    attemptsLeft = 3,
    delay = 500
): Promise<Response> {
    if (attemptsLeft <= 0) {
        return operation();
    }

    try {
        const response = await operation();

        if (Math.floor(response.status / 100) === 5) {
            throw createHTTPError.fromStatusCode(response.status, response.statusText);
        }

        return response;
    } catch (error) {
        logger.warning('retry because of error', error, { attemptsLeft, delay });
        if (attemptsLeft > 0) {
            await new Promise((resolve) => setTimeout(resolve, delay));
            return retryOnServerError(operation, attemptsLeft - 1, delay * 2);
        }

        throw error;
    }
}
